/**
 * Composable for handling drag and drop functionality for tree items
 * Manages drag state, instructions, and drag preview rendering
 *
 * @param {Object} item - The tree item being dragged/dropped
 * @param {String} mode - The drag and drop mode
 * @param {Function} emit - Event emitter function
 * @returns {Object} Drag and drop state and initialization function
 */
import { ref, h, render } from "vue";
import {
    attachInstruction,
    extractInstruction,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item";
import { pointerOutsideOfPreview } from "@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import {
    draggable,
    dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";

export function useDragAndDrop({ item, mode, emit }) {
    // Track drag state and instructions
    const isDragging = ref(false);
    const instruction = ref(null);
    const isExpanded = ref(undefined);

    /**
     * Initializes drag and drop functionality for an element
     * @param {HTMLElement} element - The draggable element
     * @param {HTMLElement} dragHandle - Element that triggers dragging
     * @returns {Function} Cleanup function
     */
    const initialise = (element, dragHandle) => {
        return combine(
            // Make element draggable
            draggable({
                element,
                dragHandle,
                getInitialData: () => item,
                onDragStart: () => {
                    isDragging.value = true;
                    isExpanded.value = item.isExpanded;

                    if (isExpanded.value) {
                        emit("update:open");
                    }
                },
                onDrop: () => {
                    isDragging.value = false;

                    if (isExpanded.value) {
                        emit("update:open");
                    }
                },
                onGenerateDragPreview: ({ nativeSetDragImage }) => {
                    // Create custom drag preview
                    setCustomNativeDragPreview({
                        getOffset: pointerOutsideOfPreview({
                            x: "16px",
                            y: "8px",
                        }),
                        render: ({ container }) => {
                            return render(
                                h(
                                    "div",
                                    {
                                        class: "bg-primary px-3 py-2 rounded text-sm font-medium",
                                    },
                                    item.name
                                ),
                                container
                            );
                        },
                        nativeSetDragImage,
                    });
                },
            }),

            // Make element a drop target
            dropTargetForElements({
                element,
                getData: ({ input, element }) =>
                    attachInstruction(
                        { id: item.id },
                        { input, element, mode }
                    ),
                onDrag: ({ self, location }) => {
                    const extractedInstruction = extractInstruction(self.data);
                    const currentParent =
                        location.current.dropTargets[0].element.getAttribute(
                            "parent"
                        );
                    const initialParent =
                        location.initial.dropTargets[0].element.getAttribute(
                            "parent"
                        );

                    instruction.value =
                        extractedInstruction.type === "make-child" ||
                        currentParent !== initialParent
                            ? extractedInstruction
                            : null;
                },
                onDragLeave: () => {
                    instruction.value = null;
                },
                onDrop: () => {
                    instruction.value = null;
                },
            })
        );
    };

    return {
        isDragging,
        instruction,
        initialise,
    };
}
